import { useState, useCallback, useMemo } from "react";
import { message } from "antd";
import {
  getList,
  autoAssign as apiAutoAssign,
  manualAssign as apiManualAssign,
  authorizeAll as apiAuthorizeAll,
  getRanges,
} from "../Services/bibService";
import { ParticipantWithBib } from "../types/bib.types";

export interface DistanceGroup {
  distance: string;
  registrations: ParticipantWithBib[];
  assigned: number;
  unassigned: number;
  range: any;
}

function groupByDistance(registrations: ParticipantWithBib[]): DistanceGroup[] {
  const map = new Map<string, DistanceGroup>();

  registrations.forEach((reg) => {
    const distance = reg.distance || (reg as any).ticket?.distance || "Unknown";
    if (!map.has(distance)) {
      map.set(distance, {
        distance,
        registrations: [],
        assigned: 0,
        unassigned: 0,
        range: null,
      });
    }
    const group = map.get(distance)!;
    group.registrations.push(reg);
    if (reg.bib_number) group.assigned++;
    else group.unassigned++;
  });

  return Array.from(map.values());
}

/**
 * Compound key for savedRanges: "distance___ticket_id"
 * Allows per-ticket BIB range tracking.
 */
export const rangeKey = (distance: string, ticket_id?: string) =>
  ticket_id ? `${distance}___${ticket_id}` : distance;

export function useBib() {
  const [distanceGroups, setDistanceGroups] = useState<DistanceGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);
  const [authorizing, setAuthorizing] = useState(false);

  // savedRanges keyed by rangeKey(distance, ticket_id)
  const [savedRanges, setSavedRanges] = useState<Record<string, any>>({});

  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 10;

  const totalRegistrations = useMemo(
    () => distanceGroups.reduce((sum, g) => sum + g.registrations.length, 0),
    [distanceGroups],
  );

  // ─── Fetch ────────────────────────────────────────────────────────────────────

  const fetchData = useCallback(
    async (
      event_id: string,
      distance: string,
      page = 1,
      ticket_id?: string,
    ) => {
      if (!event_id || !distance) return;
      setLoading(true);
      try {
        const res = await getList(
          event_id,
          distance,
          page,
          pageSize,
          ticket_id,
        );
        const registrations = res.data?.data?.data || res.data?.data || [];
        const apiTotal = res.data?.data?.total ?? registrations.length;
        setTotal(apiTotal);
        setCurrentPage(page);
        setDistanceGroups(groupByDistance(registrations));
      } catch (err: any) {
        message.error(
          err?.response?.data?.message || "Failed to load registrations.",
        );
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  // ─── Save range locally ────────────────────────────────────────────────────────

  const onRangeSaved = useCallback((key: string, rangeInfo: any) => {
    setSavedRanges((prev) => ({ ...prev, [key]: rangeInfo }));
  }, []);

  const loadSavedRanges = useCallback(
    async (
      event_id: string,
      tickets: Array<{ id: string; name: string; distance: string }>,
    ) => {
      if (!event_id) return;
      try {
        const res = await getRanges(event_id);
        const root = res.data as Record<string, unknown>;
        const list = Array.isArray(root?.data)
          ? (root.data as any[])
          : Array.isArray((root?.data as Record<string, unknown>)?.data)
            ? ((root.data as Record<string, unknown>).data as any[])
            : Array.isArray(root)
              ? (root as any[])
              : [];

        const next: Record<string, any> = {};
        list.forEach((range) => {
          if (!range?.distance) return;
          const info = {
            start: range.start_number,
            end: range.end_number,
            sort_by: range.sort_by,
            prefix: range.prefix,
            id: range.id,
          };
          next[range.distance] = info;
          tickets
            .filter((t) => t.distance === range.distance)
            .forEach((t) => {
              next[rangeKey(range.distance, t.id)] = {
                ...info,
                ticket_id: t.id,
                ticket_name: t.name,
              };
            });
        });
        setSavedRanges(next);
      } catch {
        // Non-blocking: ranges may not exist yet for new events.
      }
    },
    [],
  );

  // ─── Auto assign ──────────────────────────────────────────────────────────────

  const autoAssign = useCallback(
    async (event_id: string, distance: string, ticket_id?: string) => {
      const key = rangeKey(distance, ticket_id);
      setAssigning(key);
      try {
        await apiAutoAssign(event_id, distance, ticket_id);
        message.success(`BIBs assigned for ${distance}!`);
        await fetchData(event_id, distance, 1, ticket_id);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Auto-assign failed.");
      } finally {
        setAssigning(null);
      }
    },
    [fetchData],
  );

  // ─── Manual assign ────────────────────────────────────────────────────────────

  const manualAssign = useCallback(
    async (
      registration_id: string,
      bib_number: number,
      event_id: string,
      distance: string,
      ticket_id?: string,
    ) => {
      // Check if this registration already has a BIB assigned
      const existingReg = distanceGroups
        .flatMap((g) => g.registrations)
        .find((r) => r.id === registration_id);
      const isUpdate = !!existingReg?.bib_number;

      try {
        const res = await apiManualAssign(registration_id, bib_number, isUpdate);
        if (res.data?.status === false) {
          message.error(res.data?.message || "Manual assign failed.");
          return;
        }
        message.success(`BIB ${bib_number} ${isUpdate ? "updated" : "assigned"}.`);
        await fetchData(event_id, distance, 1, ticket_id);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Manual assign failed.");
      }
    },
    [fetchData, distanceGroups],
  );


  // ─── Authorize all ────────────────────────────────────────────────────────────

  const authorizeAll = useCallback(
    async (event_id: string, distance: string, ticket_id?: string) => {
      setAuthorizing(true);
      try {
        await apiAuthorizeAll(event_id, distance, ticket_id);
        message.success(
          "All BIBs authorized successfully. Participants will be notified via email.",
        );
        await fetchData(event_id, distance, 1, ticket_id);
      } catch (err: any) {
        message.error(err?.response?.data?.message || "Authorization failed.");
      } finally {
        setAuthorizing(false);
      }
    },
    [fetchData],
  );

  return {
    distanceGroups,
    loading,
    assigning,
    authorizing,
    savedRanges,
    totalRegistrations,
    total,
    currentPage,
    pageSize,
    fetchData,
    autoAssign,
    manualAssign,
    authorizeAll,
    onRangeSaved,
    loadSavedRanges,
  };
}
